/**
 * Flashcard Session Logic
 * 
 * Pure functions for flashcard session state, timer formatting,
 * results aggregation, and billing thresholds.
 * Extracted for testability — no Supabase or DOM dependencies.
 */

export const FLASHCARD_CONFIG = {
  MIN_WORDS_TO_START: 3,
  TIMER_INTERVAL_MS: 1000,
  MIN_BILLABLE_SECONDS: 30,
} as const;

export interface FlashcardWord {
  wordId: string;
  word: string;
  cleanWord: string;
  turnCode: string;
  skillId: string | null;
}

export interface FlashcardResult {
  wordId: string;
  word: string;
  turnCode: string;
  skillId: string | null;
  parentFeedback: 'correct' | 'incorrect';
  timeOnCardMs: number;
  cardOrder: number;
}

export interface SessionSummary {
  correct: number;
  incorrect: number;
  total: number;
  accuracy: number;
  durationSeconds: number;
  skillBreakdown: Record<string, { correct: number; incorrect: number }>;
}

export function formatSessionTime(totalSeconds: number): string {
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function calculateSessionSummary(
  results: FlashcardResult[],
  durationSeconds: number
): SessionSummary {
  const correct = results.filter(r => r.parentFeedback === 'correct').length;
  const incorrect = results.length - correct;

  const skillBreakdown: Record<string, { correct: number; incorrect: number }> = {};
  for (const r of results) {
    const key = r.skillId || 'unassigned';
    if (!skillBreakdown[key]) {
      skillBreakdown[key] = { correct: 0, incorrect: 0 };
    }
    if (r.parentFeedback === 'correct') {
      skillBreakdown[key].correct++;
    } else {
      skillBreakdown[key].incorrect++;
    }
  }

  return {
    correct,
    incorrect,
    total: results.length,
    accuracy: results.length > 0 ? correct / results.length : 0,
    durationSeconds,
    skillBreakdown,
  };
}

export function getIncorrectWords(results: FlashcardResult[]): FlashcardWord[] {
  return results
    .filter(r => r.parentFeedback === 'incorrect')
    .map(r => ({
      wordId: r.wordId,
      word: r.word,
      cleanWord: r.word.toLowerCase().replace(/[^a-z'-]/g, ''),
      turnCode: r.turnCode,
      skillId: r.skillId,
    }));
}

export function shouldBill(durationSeconds: number): boolean {
  return durationSeconds >= FLASHCARD_CONFIG.MIN_BILLABLE_SECONDS;
}

export function shuffleWords<T>(words: T[]): T[] {
  const shuffled = [...words];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
